import React from 'react';
import { MetricCard, SectionReveal, GlassCard } from '@/design-system';
import { Users, TrendingUp, Activity, AlertTriangle, TrendingDown } from 'lucide-react';
import { formatTeamWeeks, formatCurrencyFull, formatPercent } from '@/utils/formatters';

interface CapacitySummary {
  requestedTeamWeeks: number;
  allocatedTeamWeeks: number;
  availableTeamWeeks: number;
  utilizationPercentage: number;
  totalCost: number;
  allocatedCost: number;
  availableCost: number;
}

interface CapacitySummarySectionProps {
  capacitySummary: CapacitySummary | null;
  isLoading: boolean;
}

const CapacitySummarySection: React.FC<CapacitySummarySectionProps> = ({
  capacitySummary,
  isLoading,
}) => {
  const formatCostFromTeamWeeks = (teamWeeks: number, hourlyRate: number = 89) => {
    const cost = teamWeeks * 40 * hourlyRate;
    return formatCurrencyFull(cost);
  };

  const getUtilizationColor = (percentage: number): 'blue' | 'emerald' | 'amber' | 'rose' => {
    if (percentage > 100) return 'rose';
    if (percentage > 90) return 'amber';
    if (percentage > 70) return 'emerald';
    return 'blue';
  };

  const getUtilizationStatus = (percentage: number) => {
    if (percentage > 100) return 'Over-allocated';
    if (percentage > 90) return 'High utilization';
    if (percentage > 70) return 'Good utilization';
    return 'Under-utilized';
  };

  const getGapColor = (shortfall: number): 'amber' | 'rose' | 'emerald' => {
    if (shortfall > 0) return 'rose';
    if (shortfall < -10) return 'emerald';
    return 'amber';
  };

  const getGapStatus = (shortfall: number) => {
    if (shortfall > 0) return 'Capacity Gap';
    if (shortfall < -10) return 'Surplus';
    return 'Balanced';
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {[...Array(4)].map((_, i) => (
          <SectionReveal key={i} delay={i * 0.05}>
            <GlassCard padding="lg">
              <div className="animate-pulse">
                <div className="h-4 bg-text-tertiary/20 rounded w-3/4 mb-3"></div>
                <div className="h-8 bg-text-tertiary/20 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-text-tertiary/20 rounded w-full mt-4"></div>
              </div>
            </GlassCard>
          </SectionReveal>
        ))}
      </div>
    );
  }

  if (!capacitySummary) {
    return (
      <SectionReveal delay={0}>
        <GlassCard padding="lg" className="mb-6">
          <div className="text-center text-muted-foreground/60 py-4">
            No capacity data available for this Investible Structure
          </div>
        </GlassCard>
      </SectionReveal>
    );
  }

  // Compute derived values from backend data
  const requestedCapacity = capacitySummary.requestedTeamWeeks;
  const allocatedCapacity = capacitySummary.allocatedTeamWeeks;
  const shortfall = requestedCapacity - allocatedCapacity;
  const allocationPercentage = requestedCapacity > 0
    ? (allocatedCapacity / requestedCapacity) * 100
    : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
      {/* Requested Capacity */}
      <SectionReveal delay={0}>
        <MetricCard
          title="Requested Capacity"
          value={formatTeamWeeks(requestedCapacity)}
          subtitle={formatCostFromTeamWeeks(requestedCapacity)}
          icon={<Users className="w-4 h-4" />}
          iconColor="blue"
          progress={100}
        />
      </SectionReveal>

      {/* Allocated Capacity */}
      <SectionReveal delay={0.05}>
        <MetricCard
          title="Allocated Capacity"
          value={formatTeamWeeks(allocatedCapacity)}
          subtitle={formatCostFromTeamWeeks(allocatedCapacity)}
          icon={<TrendingUp className="w-4 h-4" />}
          iconColor="emerald"
          progress={allocationPercentage}
        />
      </SectionReveal>

      {/* Utilization Percentage */}
      <SectionReveal delay={0.1}>
        <MetricCard
          title="Utilization"
          value={formatPercent(capacitySummary.utilizationPercentage)}
          subtitle={getUtilizationStatus(capacitySummary.utilizationPercentage)}
          icon={<Activity className="w-4 h-4" />}
          iconColor={getUtilizationColor(capacitySummary.utilizationPercentage)}
          progress={Math.min(capacitySummary.utilizationPercentage, 100)}
        />
      </SectionReveal>

      {/* Capacity Gap */}
      <SectionReveal delay={0.15}>
        <MetricCard
          title="Capacity Gap"
          value={`${shortfall > 0 ? '+' : ''}${formatTeamWeeks(shortfall)}`}
          subtitle={getGapStatus(shortfall)}
          icon={shortfall > 0 ? <AlertTriangle className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          iconColor={getGapColor(shortfall)}
          progress={Math.min(Math.abs(shortfall) / Math.max(requestedCapacity, 1) * 100, 100)}
        />
      </SectionReveal>
    </div>
  );
};

export default CapacitySummarySection;
